// Node Configuration Inspector Panel (PRD §1.9, WP-08).
// Property editor for all 17 node types.
import { useI18n } from '../../lib/i18n';
import { useLabels } from '../../lib/crm';
import { useTemplates } from '../../lib/whatsappApi';
import {
  NODE_CATALOGUE,
  type FlowNodeType,
  type FlowNode,
  type ConditionRule,
  type ConditionOperator,
  type InteractiveButton,
} from '../../lib/flows';
import { Button } from '../ui/Button';
import { CloseIcon, TrashIcon, PlusIcon } from '../icons';

interface NodeConfigPanelProps {
  node: FlowNode | null;
  onUpdate: (id: string, data: any) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

const CONDITION_OPERATORS: { id: ConditionOperator; label: string; labelKey: string }[] = [
  { id: 'equals', label: 'Equals (=)', labelKey: 'flow.op.equals' },
  { id: 'not_equals', label: 'Not Equals (≠)', labelKey: 'flow.op.notEquals' },
  { id: 'contains', label: 'Contains', labelKey: 'flow.op.contains' },
  { id: 'greater_than', label: 'Greater Than (>)', labelKey: 'flow.op.greaterThan' },
  { id: 'less_than', label: 'Less Than (<)', labelKey: 'flow.op.lessThan' },
  { id: 'in_list', label: 'In List', labelKey: 'flow.op.inList' },
  { id: 'has_label', label: 'Has CRM Label', labelKey: 'flow.op.hasLabel' },
  { id: 'time_between', label: 'Current Time Between (HH:MM-HH:MM)', labelKey: 'flow.op.timeBetween' },
];

export default function NodeConfigPanel({
  node,
  onUpdate,
  onDelete,
  onClose,
}: NodeConfigPanelProps) {
  const { t } = useI18n();
  const { labels } = useLabels();
  const { items: templates } = useTemplates();

  if (!node) return null;

  const nodeType = node.type as FlowNodeType;
  const data = node.data || {};
  const meta = NODE_CATALOGUE.find((item) => item.type === nodeType) || NODE_CATALOGUE[0];
  const title = t(meta.labelKey) || meta.defaultLabel;

  const update = (patch: Record<string, any>) => {
    onUpdate(node.id, { ...data, ...patch });
  };

  return (
    <div className="flex flex-col h-full w-80 sm:w-96 border-s border-line bg-white dark:border-slate-800 dark:bg-slate-900 shadow-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-4 py-3 dark:border-slate-800">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl" aria-hidden>{meta.icon}</span>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-ink dark:text-white">
              {title}
            </h3>
            <span className="text-[11px] text-ink-3 dark:text-slate-400 font-mono">
              ID: {node.id}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close') || 'Close'}
          className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Editor Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
        {/* 1. TEXT MESSAGE */}
        {nodeType === 'text' && (
          <div>
            <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
              {t('flow.config.textBody') || 'Message Text'} *
            </label>
            <textarea
              rows={6}
              value={data.text || ''}
              onChange={(e) => update({ text: e.target.value })}
              placeholder={t('flow.config.textPlaceholder') || 'Type your message text here...'}
              className="w-full rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            />
            <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-400">
              {t('flow.config.variablesSupported') || 'Variables supported: {{name}}, {{phone}}, {{var:key}}.'}
            </p>
          </div>
        )}

        {/* 2. MEDIA (IMAGE, VIDEO, AUDIO, FILE) */}
        {['image', 'video', 'audio', 'file'].includes(nodeType) && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('flow.config.mediaUrl') || 'Media URL / Asset Link'}
              </label>
              <input
                type="text"
                value={data.mediaUrl || ''}
                onChange={(e) => update({ mediaUrl: e.target.value })}
                placeholder={t('flow.config.mediaUrlPlaceholder') || 'https://example.com/asset.jpg'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>

            {nodeType === 'file' && (
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                  {t('flow.config.fileName') || 'Document Filename'}
                </label>
                <input
                  type="text"
                  value={data.fileName || ''}
                  onChange={(e) => update({ fileName: e.target.value })}
                  placeholder={t('flow.config.fileNamePlaceholder') || 'invoice.pdf'}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            )}

            {nodeType !== 'audio' && (
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                  {t('flow.config.caption') || 'Optional Caption'}
                </label>
                <input
                  type="text"
                  value={data.caption || ''}
                  onChange={(e) => update({ caption: e.target.value })}
                  placeholder={t('flow.config.captionPlaceholder') || 'Caption text below media...'}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            )}
          </div>
        )}

        {/* 3. LOCATION PIN */}
        {nodeType === 'location' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.latitude') || 'Latitude'}</label>
                <input
                  type="number"
                  step="any"
                  value={data.latitude ?? 23.588}
                  onChange={(e) => update({ latitude: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.longitude') || 'Longitude'}</label>
                <input
                  type="number"
                  step="any"
                  value={data.longitude ?? 58.3829}
                  onChange={(e) => update({ longitude: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.locationName') || 'Location Name'}</label>
              <input
                type="text"
                value={data.name || ''}
                onChange={(e) => update({ name: e.target.value })}
                placeholder={t('flow.config.locationPlaceholder') || 'Our Store / Office'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.addressText') || 'Address Text'}</label>
              <input
                type="text"
                value={data.address || ''}
                onChange={(e) => update({ address: e.target.value })}
                placeholder={t('flow.config.addressPlaceholder') || 'Street address or directions'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}

        {/* 4. AI REPLY */}
        {nodeType === 'ai_reply' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('flow.config.systemPromptOverride') || 'Custom System Prompt Override'}
              </label>
              <textarea
                rows={4}
                value={data.systemPrompt || ''}
                onChange={(e) => update({ systemPrompt: e.target.value })}
                placeholder={t('flow.config.systemPromptPlaceholder') || 'Leave blank to use default trained AI agent system prompt...'}
                className="w-full rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1 font-semibold text-ink dark:text-slate-200">
                <span>{t('flow.config.creativity') || 'Creativity / Temperature'}</span>
                <span className="font-mono text-brand">{data.temperature ?? 0.7}</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={data.temperature ?? 0.7}
                onChange={(e) => update({ temperature: parseFloat(e.target.value) })}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* 5. USER INPUT QUESTION */}
        {nodeType === 'user_input_flow' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.questionPrompt') || 'Question Prompt *'}</label>
              <textarea
                rows={3}
                value={data.prompt || ''}
                onChange={(e) => update({ prompt: e.target.value })}
                placeholder={t('flow.config.questionPromptPlaceholder') || 'Please enter your email:'}
                className="w-full rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('flow.config.storeVariable') || 'Store in Variable Name *'}
              </label>
              <input
                type="text"
                value={data.variableName || ''}
                onChange={(e) => update({ variableName: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder={t('flow.config.storeVariablePlaceholder') || 'customer_email'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 font-mono text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.validationType') || 'Validation Type'}</label>
              <select
                value={data.validationType || 'text'}
                onChange={(e) => update({ validationType: e.target.value })}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <option value="text">{t('flow.config.valText') || 'Any Text'}</option>
                <option value="email">{t('flow.config.valEmail') || 'Email Address'}</option>
                <option value="number">{t('flow.config.valNumber') || 'Numeric'}</option>
                <option value="phone">{t('flow.config.valPhone') || 'Phone Number'}</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.retryError') || 'Retry Error Message'}</label>
              <input
                type="text"
                value={data.retryMessage || ''}
                onChange={(e) => update({ retryMessage: e.target.value })}
                placeholder={t('flow.config.retryErrorPlaceholder') || 'Invalid format. Please try again:'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}

        {/* 6. INTERACTIVE MESSAGE (BUTTONS) */}
        {nodeType === 'interactive_message' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.msgBody') || 'Message Body *'}</label>
              <textarea
                rows={3}
                value={data.body || ''}
                onChange={(e) => update({ body: e.target.value })}
                placeholder={t('flow.config.msgBodyPlaceholder') || 'How can we assist you today?'}
                className="w-full rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.footerText') || 'Optional Footer Text'}</label>
              <input
                type="text"
                value={data.footer || ''}
                onChange={(e) => update({ footer: e.target.value })}
                placeholder={t('flow.config.footerPlaceholder') || 'Choose an option below'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-semibold text-ink dark:text-slate-200">
                  {t('flow.config.quickReplyMax') || 'Quick Reply Buttons (Max 3)'}
                </label>
                {(data.buttons?.length || 0) < 3 && (
                  <button
                    type="button"
                    onClick={() => {
                      const current = (data.buttons || []) as InteractiveButton[];
                      const newId = `btn_${Date.now()}`;
                      update({ buttons: [...current, { id: newId, title: `${t('flow.config.optionN', { n: current.length + 1 }) || `Option ${current.length + 1}`}` }] });
                    }}
                    className="flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline"
                  >
                    <PlusIcon className="h-3 w-3" /> {t('flow.config.addButton') || 'Add Button'}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(data.buttons || []).map((btn: InteractiveButton, idx: number) => (
                  <div key={btn.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={btn.title}
                      maxLength={20}
                      onChange={(e) => {
                        const next = [...data.buttons];
                        next[idx] = { ...btn, title: e.target.value };
                        update({ buttons: next });
                      }}
                      className="flex-1 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        update({ buttons: data.buttons.filter((b: InteractiveButton) => b.id !== btn.id) });
                      }}
                      className="rounded p-1 text-ink-4 hover:text-danger"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 7. TEMPLATE MESSAGE */}
        {nodeType === 'template_message' && (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300 text-[11px]">
              {t('flow.config.templateApprovedWindow') || 'Approved templates can be delivered even after the 24-hour messaging window expires.'}
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.templateName') || 'Approved Template Name *'}</label>
              {templates && templates.length > 0 ? (
                <select
                  value={data.templateName || ''}
                  onChange={(e) => {
                    const match = templates.find((t) => t.name === e.target.value);
                    update({
                      templateName: e.target.value,
                      templateLanguage: match?.language || 'en',
                    });
                  }}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="">{t('flow.config.selectTemplate') || '-- Select Template --'}</option>
                  {templates
                    .filter((t) => t.status === 'APPROVED')
                    .map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.name} ({t.language})
                      </option>
                    ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={data.templateName || ''}
                  onChange={(e) => update({ templateName: e.target.value })}
                  placeholder={t('flow.config.templateNamePlaceholder') || 'welcome_template'}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              )}
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.languageCode') || 'Language Code'}</label>
              <input
                type="text"
                value={data.templateLanguage || 'en'}
                onChange={(e) => update({ templateLanguage: e.target.value })}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}

        {/* 8. CTA URL BUTTON */}
        {nodeType === 'cta_url_button' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.msgBody') || 'Message Body *'}</label>
              <textarea
                rows={3}
                value={data.body || ''}
                onChange={(e) => update({ body: e.target.value })}
                placeholder={t('flow.config.ctaUrlPlaceholder') || 'Visit our portal for latest updates'}
                className="w-full rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.buttonLabel') || 'Button Label *'}</label>
              <input
                type="text"
                value={data.buttonText || ''}
                onChange={(e) => update({ buttonText: e.target.value })}
                placeholder={t('flow.config.buttonLabelPlaceholder') || 'Open Website'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.targetUrl') || 'Target Web URL *'}</label>
              <input
                type="url"
                value={data.url || ''}
                onChange={(e) => update({ url: e.target.value })}
                placeholder={t('flow.config.targetUrlPlaceholder') || 'https://yourstore.com/promo'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}

        {/* 9. CONDITION BRANCH */}
        {nodeType === 'condition' && (
          <div className="space-y-3">
            <div className="rounded-xl border border-line bg-surface/50 p-2.5 text-[11px] text-ink-3 dark:border-slate-700 dark:bg-slate-800">
              {t('flow.config.conditionBranches') || 'Output branches: connect green True handle or red False handle to following nodes.'}
            </div>

            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.matchLogic') || 'Match Logic'}</label>
              <div className="grid grid-cols-2 gap-2">
                {(['and', 'or'] as const).map((op) => (
                  <button
                    key={op}
                    type="button"
                    onClick={() => update({ logicalOperator: op })}
                    className={`rounded-lg border px-3 py-1.5 font-bold uppercase transition-all ${
                      data.logicalOperator === op
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-line bg-white text-ink-2 dark:border-slate-700 dark:bg-slate-800'
                    }`}
                  >
                    {t('flow.config.allRules', { op }) || `All Rules (${op})`}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between font-semibold text-ink dark:text-slate-200">
                <span>{t('flow.config.rulesCount', { count: (data.rules || []).length }) || `Rules (${(data.rules || []).length})`}</span>
                <button
                  type="button"
                  onClick={() => {
                    const current = (data.rules || []) as ConditionRule[];
                    update({
                      rules: [...current, { field: 'variables.email', operator: 'not_equals', value: '' }],
                    });
                  }}
                  className="flex items-center gap-1 text-[11px] text-brand hover:underline"
                >
                  <PlusIcon className="h-3 w-3" /> {t('flow.config.addRule') || 'Add Rule'}
                </button>
              </div>

              {(data.rules || []).map((rule: ConditionRule, idx: number) => (
                <div key={idx} className="rounded-xl border border-line bg-surface/40 p-2.5 space-y-2 dark:border-slate-800">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-ink-2 dark:text-slate-300">{t('flow.config.ruleN', { n: idx + 1 }) || `Rule #${idx + 1}`}</span>
                    <button
                      type="button"
                      onClick={() => {
                        update({ rules: data.rules.filter((_: any, i: number) => i !== idx) });
                      }}
                      className="text-danger hover:underline text-[10px]"
                    >
                      {t('common.remove') || 'Remove'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={rule.field}
                    onChange={(e) => {
                      const next = [...data.rules];
                      next[idx] = { ...rule, field: e.target.value };
                      update({ rules: next });
                    }}
                    placeholder={t('flow.config.ruleFieldPlaceholder') || 'field e.g. variables.city or labels'}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1 text-xs text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                  <select
                    value={rule.operator}
                    onChange={(e) => {
                      const next = [...data.rules];
                      next[idx] = { ...rule, operator: e.target.value as ConditionOperator };
                      update({ rules: next });
                    }}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1 text-xs text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {CONDITION_OPERATORS.map((op) => (
                      <option key={op.id} value={op.id}>
                        {t(op.labelKey) || op.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={rule.value}
                    onChange={(e) => {
                      const next = [...data.rules];
                      next[idx] = { ...rule, value: e.target.value };
                      update({ rules: next });
                    }}
                    placeholder={t('flow.config.targetValuePlaceholder') || 'target value'}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1 text-xs text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 10. ENROLL IN SEQUENCE */}
        {nodeType === 'subscribe_sequence' && (
          <div>
            <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
              {t('flow.config.sequenceId') || 'Sequence Campaign ID *'}
            </label>
            <input
              type="text"
              value={data.sequenceId || ''}
              onChange={(e) => update({ sequenceId: e.target.value })}
              placeholder={t('flow.config.sequencePlaceholder') || 'sequence_campaign_id'}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 font-mono text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>
        )}

        {/* 11. ASSIGN / REMOVE LABELS */}
        {nodeType === 'label_assign' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('flow.config.attachLabels') || 'Attach CRM Labels'}
              </label>
              <select
                multiple
                value={data.addLabelIds || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                  update({ addLabelIds: selected });
                }}
                className="w-full rounded-xl border border-line bg-white p-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 h-28"
              >
                {labels.map((lbl) => (
                  <option key={lbl.id} value={lbl.id}>
                    + {lbl.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-ink-3 dark:text-slate-400">{t('flow.config.multiLabelHint') || 'Hold Ctrl/Cmd to select multiple labels.'}</p>
            </div>
          </div>
        )}

        {/* 12. RANDOM NUMBER GENERATOR */}
        {nodeType === 'random_number' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.minValue') || 'Min Value'}</label>
                <input
                  type="number"
                  value={data.min ?? 1}
                  onChange={(e) => update({ min: parseInt(e.target.value, 10) || 0 })}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.maxValue') || 'Max Value'}</label>
                <input
                  type="number"
                  value={data.max ?? 100}
                  onChange={(e) => update({ max: parseInt(e.target.value, 10) || 100 })}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.storeInVariable') || 'Store in Variable'}</label>
              <input
                type="text"
                value={data.variableName || ''}
                onChange={(e) => update({ variableName: e.target.value })}
                placeholder={t('flow.config.randomSplitPlaceholder') || 'random_split'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 font-mono text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}

        {/* 13. HTTP API & GOOGLE SHEET FETCH */}
        {nodeType === 'http_api' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.httpApiId') || 'Outbound HTTP API ID (WP-13) *'}</label>
              <input
                type="text"
                value={data.apiId || ''}
                onChange={(e) => update({ apiId: e.target.value })}
                placeholder={t('flow.config.httpApiPlaceholder') || 'http_api_id'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 font-mono text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}

        {nodeType === 'google_sheet_fetch' && (
          <div className="space-y-3">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.sheetFetchId') || 'Google Sheet Fetch Campaign ID *'}</label>
              <input
                type="text"
                value={data.fetchId || ''}
                onChange={(e) => update({ fetchId: e.target.value })}
                placeholder={t('flow.config.sheetFetchPlaceholder') || 'sheet_fetch_id'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 font-mono text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.config.keyColumn') || 'Key Column Variable'}</label>
              <input
                type="text"
                value={data.keyVariable || 'phone'}
                onChange={(e) => update({ keyVariable: e.target.value })}
                placeholder={t('flow.config.phonePlaceholder') || 'phone'}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 font-mono text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-line px-4 py-3 dark:border-slate-800">
        <Button
          variant="danger"
          size="xs"
          onClick={() => onDelete(node.id)}
          leadingIcon={<TrashIcon className="h-3.5 w-3.5" />}
        >
          {t('flow.config.deleteNode') || 'Delete Block'}
        </Button>
        <Button variant="secondary" size="xs" onClick={onClose}>
          {t('common.done') || 'Done'}
        </Button>
      </div>
    </div>
  );
}
